import { useEffect, useState } from 'react';

/**
 * The `beforeinstallprompt` event isn't in the standard lib DOM types yet, so we
 * describe the bits we use. The browser fires it when the app is installable;
 * we stash it (in index.html, before React mounts, to avoid missing an early
 * fire) and replay it from a real user gesture (the sidebar button).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    __brewInstall?: { evt: BeforeInstallPromptEvent | null };
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * "Install app" row for the sidebar. Stays visible whenever the app isn't
 * already installed. If the browser has handed us its install prompt we fire it
 * natively; otherwise (prompt not yet ready, or a browser like Firefox/iOS that
 * doesn't expose it) we show a short how-to so the option is never a dead end.
 */
export function InstallAppButton(): JSX.Element | null {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    () => window.__brewInstall?.evt ?? null,
  );
  const [installed, setInstalled] = useState(isStandalone());
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    function syncDeferred(): void {
      setDeferred(window.__brewInstall?.evt ?? null);
    }
    function onInstalled(): void {
      setInstalled(true);
      setDeferred(null);
    }
    // Custom events dispatched by the early capture in index.html, plus the
    // native events as a fallback in case this mounts before that script ran.
    window.addEventListener('brew:installable', syncDeferred);
    window.addEventListener('brew:installed', onInstalled);
    window.addEventListener('beforeinstallprompt', syncDeferred);
    window.addEventListener('appinstalled', onInstalled);
    // Catch an event that fired between module load and this effect.
    syncDeferred();
    return () => {
      window.removeEventListener('brew:installable', syncDeferred);
      window.removeEventListener('brew:installed', onInstalled);
      window.removeEventListener('beforeinstallprompt', syncDeferred);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  async function handleClick(): Promise<void> {
    const evt = deferred ?? window.__brewInstall?.evt ?? null;
    if (evt) {
      await evt.prompt();
      const { outcome } = await evt.userChoice;
      if (window.__brewInstall) window.__brewInstall.evt = null;
      setDeferred(null);
      if (outcome === 'accepted') setInstalled(true);
      return;
    }
    // No native prompt available — reveal manual instructions instead.
    setShowHint((s) => !s);
  }

  return (
    <>
      <button
        type="button"
        className="snav__item snav__item--install"
        onClick={() => void handleClick()}
        data-tip="Install app"
        aria-label="Install app"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="snav__label"><span className="snav__label-in">Install app</span></span>
      </button>
      {showHint && (
        <p className="snav__install-hint">
          Open your browser menu and choose <strong>“Install Breww”</strong> (desktop Chrome/Edge show an
          install icon in the address bar). On iPhone: <strong>Share → Add to Home Screen</strong>.
        </p>
      )}
    </>
  );
}
